/**
 * SuggestBusinessModal — formulario corto cuando un usuario no encuentra un negocio.
 *
 * Crea entrada source="user_suggestion" en pending_review (no es el dueño,
 * no se le crea cuenta, ningún email automático).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import TurnstileWidget from "./TurnstileWidget";
import { X, CheckCircle, MapPin } from "@phosphor-icons/react";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function SuggestBusinessModal({ onClose, defaultCity = "" }) {
  const [meta, setMeta] = useState({ sectors: [] });
  const [form, setForm] = useState({
    name: "", city: defaultCity, sectors: [], website_url: "", note: "", suggester_email: "",
  });
  const [tToken, setTToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/marketplace/businesses/form-meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const toggleSector = (id) => setForm((f) => ({
    ...f, sectors: f.sectors.includes(id) ? f.sectors.filter((x) => x !== id) : [...f.sectors, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/marketplace/businesses/suggest", {
        ...form, turnstile_token: tToken, honeypot: "",
        suggester_email: form.suggester_email || null,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo enviar.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="suggest-modal">
      <div className="relative ag-card max-w-md w-full p-6 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--ag-text-dim)] hover:text-white"
                data-testid="suggest-close"><X size={18} /></button>
        {done ? (
          <div className="text-center py-6" data-testid="suggest-success">
            <CheckCircle size={32} weight="fill" className="text-emerald-300 mx-auto" />
            <h3 className="font-display text-2xl font-bold mt-3 text-[var(--ag-text)]">¡Gracias!</h3>
            <p className="text-[var(--ag-text-dim)] text-sm mt-2">
              Hemos recibido tu sugerencia. Nuestro equipo la revisará pronto.
            </p>
            <button onClick={onClose} className="ag-btn-gold mt-5 px-5 py-2 text-xs uppercase tracking-wider"
                    data-testid="suggest-close-success">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="suggest-form">
            <div>
              <MapPin size={20} className="text-[var(--ag-gold)]" />
              <h3 className="font-display text-2xl font-bold tracking-tighter mt-2 text-[var(--ag-text)]">Sugerir negocio</h3>
              <p className="text-[var(--ag-text-dim)] text-xs mt-1">¿Conoces un negocio que falta? Cuéntanoslo.</p>
            </div>
            <Input placeholder="Nombre del negocio *" required minLength={2}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="suggest-name" />
            <Input placeholder="Ciudad *" required minLength={2}
              value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid="suggest-city" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] mb-1.5">Categorías</div>
              <div className="flex flex-wrap gap-1.5">
                {meta.sectors.map((s) => (
                  <button type="button" key={s.id} onClick={() => toggleSector(s.id)}
                    className={`px-2.5 py-1 rounded-sm text-[10px] border transition-colors ${
                      form.sectors.includes(s.id)
                        ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/15 text-[var(--ag-gold)]"
                        : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
                    data-testid={`suggest-sector-${s.id}`}>
                    {s.label_es}
                  </button>
                ))}
              </div>
            </div>
            <Input type="url" placeholder="Sitio web (opcional)" value={form.website_url}
              onChange={(e) => setForm({ ...form, website_url: e.target.value })} />
            <Input type="email" placeholder="Tu email (opcional · para avisarte cuando esté online)"
              value={form.suggester_email}
              onChange={(e) => setForm({ ...form, suggester_email: e.target.value })} />
            <textarea placeholder="Notas adicionales (opcional)" rows={2}
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="w-full px-3 py-2.5 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none" />
            <TurnstileWidget onToken={setTToken} />
            {error && <div className="text-xs text-red-400" data-testid="suggest-error">{error}</div>}
            <button type="submit" disabled={busy}
              className="ag-btn-gold w-full py-3 text-xs font-semibold tracking-wider uppercase"
              data-testid="suggest-submit">
              {busy ? "Enviando…" : "Enviar sugerencia"}
            </button>
            <p className="text-[10px] text-[var(--ag-text-dim)] text-center leading-relaxed" data-testid="suggest-legal-notice">
              Al enviar aceptas la <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Política de privacidad</a> y los <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Términos</a>. Tu sugerencia será revisada por nuestro equipo.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const Input = (props) => (
  <input {...props}
    className={`w-full h-11 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none ${props.className||""}`} />
);
